import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Phone, CheckCircle2, AlertCircle, Minus, Plus, Clock } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { SERVICES, CITIES, BUSINESS } from "@/data/site";
import { api, formatApiError, trackEvent } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STEPS = ["Service", "Date & Time", "Children", "Location", "Contact"];
const DURATIONS = [4, 5, 6, 8, 10, 12];
const TIMES = ["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–8pm)", "Night (8pm–12am)", "Overnight"];

const emptyForm = {
  service: "", start_date: "", start_time: "", duration_hours: 4,
  num_children: 1, child_ages: "", special_instructions: "",
  city: "", address: "", contact_name: "", phone: "", email: "", preferred_contact: "phone",
};

export default function Book() {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("form"); // form | submitting | success | error
  const [startedTracked, setStartedTracked] = useState(false);

  const set = (k, v) => {
    if (!startedTracked) { trackEvent("form_start", { form: "booking" }); setStartedTracked(true); }
    setForm((f) => ({ ...f, [k]: v }));
  };

  const today = new Date().toISOString().split("T")[0];
  const afterHours = (() => {
    const h = new Date().getHours();
    return h < 8 || h >= 18;
  })();

  const canNext = () => {
    if (step === 0) return !!form.service;
    if (step === 1) return !!form.start_date && !!form.start_time && form.duration_hours >= 4;
    if (step === 2) return form.num_children >= 1;
    if (step === 3) return !!form.city;
    if (step === 4) return form.contact_name && form.phone && /\S+@\S+\.\S+/.test(form.email);
    return false;
  };

  const next = () => {
    if (!canNext()) { toast.error("Please complete this step before continuing."); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const selectService = (name) => {
    set("service", name);
    setTimeout(() => setStep(1), 240);
  };

  const submit = async () => {
    if (!canNext()) { toast.error("Please fill in your contact details."); return; }
    setStatus("submitting");
    try {
      await api.post("/bookings", { ...form, duration_hours: Number(form.duration_hours), num_children: Number(form.num_children) });
      trackEvent("form_complete", { form: "booking", service: form.service });
      setStatus("success");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <>
        <Seo title="Request Received" description="Your sitter request has been received." />
        <div className="container-mbf py-24 max-w-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-espresso mb-4" data-testid="booking-success-title">Your request is in — thank you!</h1>
          <p className="text-base text-stoneish leading-relaxed mb-2">
            We've received your request for <strong className="text-espresso">{form.service}</strong>. A member of our team will personally confirm your sitter and the details shortly.
          </p>
          {afterHours && (
            <div className="mt-6 bg-tinted border border-[#F1E6D0] rounded-xl p-5 flex items-start gap-3 text-left" data-testid="booking-afterhours-notice">
              <Clock className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
              <p className="text-sm text-stoneish">It's currently outside our office hours. We'll follow up first thing during business hours. For urgent needs, please call us directly.</p>
            </div>
          )}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={BUSINESS.phoneHref} className="btn-accent" data-testid="booking-success-phone"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
            <Link to="/" className="btn-outline" data-testid="booking-success-home">Back to home</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Request a Sitter" description="Request a trusted, screened sitter in DFW. Choose your service, date, children and location — our team confirms every detail." />
      <div className="bg-parchment-2 border-b border-[#F1E6D0]">
        <div className="container-mbf py-12 md:py-14">
          <p className="overline mb-3">Request a sitter</p>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-espresso tracking-tight">Let's find the right caregiver</h1>
          <p className="mt-3 text-stoneish max-w-xl">Takes about two minutes. A real person confirms every detail — {BUSINESS.minHours}-hour minimum applies.</p>
        </div>
      </div>

      <div className="container-mbf py-12 md:py-16 max-w-3xl">
        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((label, i) => (
              <div key={label} className="flex flex-col items-center flex-1" data-testid={`booking-progress-${i}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${i < step ? "bg-sage border-sage text-white" : i === step ? "bg-espresso border-espresso text-parchment" : "bg-white border-[#F1E6D0] text-stoneish"}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`hidden sm:block text-xs mt-2 ${i === step ? "text-espresso font-semibold" : "text-stoneish"}`}>{label}</span>
              </div>
            ))}
          </div>
          <div className="h-1.5 bg-[#F1E6D0] rounded-full overflow-hidden">
            <div className="h-full bg-terracotta transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={reduce ? false : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reduce ? {} : { opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="card-warm p-7 md:p-10"
          >
            {step === 0 && (
              <div>
                <h2 className="font-serif text-2xl text-espresso mb-1">What kind of care do you need?</h2>
                <p className="text-sm text-stoneish mb-6">Choose the service that fits best.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SERVICES.map((s) => (
                    <button key={s.id} onClick={() => selectService(s.name)} className={`text-left p-5 rounded-xl border-2 transition-all ${form.service === s.name ? "border-terracotta bg-terracotta/5" : "border-[#F1E6D0] hover:border-terracotta/40"}`} data-testid={`booking-service-${s.id}`}>
                      <div className="font-semibold text-espresso">{s.name}</div>
                      <div className="text-xs text-stoneish mt-1">{s.tagline}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 className="font-serif text-2xl text-espresso mb-1">When do you need care?</h2>
                <p className="text-sm text-stoneish mb-6">A {BUSINESS.minHours}-hour minimum applies to every booking.</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="start_date" className="mb-2 block text-espresso">Start date</Label>
                    <Input id="start_date" type="date" min={today} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="booking-date-input" />
                  </div>
                  <div>
                    <Label className="mb-2 block text-espresso">Time of day</Label>
                    <Select value={form.start_time} onValueChange={(v) => set("start_time", v)}>
                      <SelectTrigger data-testid="booking-time-trigger"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-5">
                  <Label className="mb-2 block text-espresso">Duration (hours)</Label>
                  <div className="flex flex-wrap gap-2">
                    {DURATIONS.map((d) => (
                      <button key={d} onClick={() => set("duration_hours", d)} className={`px-5 py-2.5 rounded-full border-2 text-sm font-semibold transition-colors ${form.duration_hours === d ? "border-terracotta bg-terracotta text-white" : "border-[#F1E6D0] text-espresso hover:border-terracotta/40"}`} data-testid={`booking-duration-${d}`}>{d} hrs</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="font-serif text-2xl text-espresso mb-1">Tell us about the children</h2>
                <p className="text-sm text-stoneish mb-6">This helps us match the right caregiver.</p>
                <Label className="mb-2 block text-espresso">Number of children</Label>
                <div className="flex items-center gap-4 mb-6">
                  <button onClick={() => set("num_children", Math.max(1, form.num_children - 1))} className="w-11 h-11 rounded-full border-2 border-[#F1E6D0] flex items-center justify-center hover:border-terracotta transition-colors" data-testid="booking-children-minus"><Minus className="w-4 h-4" /></button>
                  <span className="font-serif text-3xl text-espresso w-10 text-center" data-testid="booking-children-count">{form.num_children}</span>
                  <button onClick={() => set("num_children", form.num_children + 1)} className="w-11 h-11 rounded-full border-2 border-[#F1E6D0] flex items-center justify-center hover:border-terracotta transition-colors" data-testid="booking-children-plus"><Plus className="w-4 h-4" /></button>
                </div>
                <div className="mb-5">
                  <Label htmlFor="child_ages" className="mb-2 block text-espresso">Children's ages <span className="text-stoneish font-normal">(optional)</span></Label>
                  <Input id="child_ages" placeholder="e.g. 4 and 7" value={form.child_ages} onChange={(e) => set("child_ages", e.target.value)} data-testid="booking-ages-input" />
                </div>
                <div>
                  <Label htmlFor="special" className="mb-2 block text-espresso">Special instructions <span className="text-stoneish font-normal">(optional)</span></Label>
                  <Textarea id="special" placeholder="Allergies, routines, anything we should know…" value={form.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} data-testid="booking-instructions-input" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h2 className="font-serif text-2xl text-espresso mb-1">Where is the care needed?</h2>
                <p className="text-sm text-stoneish mb-6">We serve families across the DFW metroplex.</p>
                <div className="mb-5">
                  <Label className="mb-2 block text-espresso">City / area</Label>
                  <Select value={form.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger data-testid="booking-city-trigger"><SelectValue placeholder="Select your city" /></SelectTrigger>
                    <SelectContent>
                      {CITIES.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                      <SelectItem value="Other DFW area">Other DFW area</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="address" className="mb-2 block text-espresso">Address or venue <span className="text-stoneish font-normal">(optional)</span></Label>
                  <Input id="address" placeholder="Street address, hotel, or venue name" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="booking-address-input" />
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="font-serif text-2xl text-espresso mb-1">How can we reach you?</h2>
                <p className="text-sm text-stoneish mb-6">We'll confirm your sitter and the details personally.</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="contact_name" className="mb-2 block text-espresso">Full name</Label>
                    <Input id="contact_name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="booking-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="mb-2 block text-espresso">Phone</Label>
                    <Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone-input" />
                  </div>
                </div>
                <div className="mt-5">
                  <Label htmlFor="email" className="mb-2 block text-espresso">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email-input" />
                </div>
                <div className="mt-5">
                  <Label className="mb-2 block text-espresso">Preferred contact method</Label>
                  <div className="flex gap-2">
                    {["phone", "email", "text"].map((m) => (
                      <button key={m} onClick={() => set("preferred_contact", m)} className={`px-5 py-2.5 rounded-full border-2 text-sm font-semibold capitalize transition-colors ${form.preferred_contact === m ? "border-terracotta bg-terracotta text-white" : "border-[#F1E6D0] text-espresso hover:border-terracotta/40"}`} data-testid={`booking-contact-${m}`}>{m}</button>
                    ))}
                  </div>
                </div>
                {status === "error" && (
                  <div className="mt-5 bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3" data-testid="booking-error">
                    <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">Something went wrong submitting your request. Please try again or call us at {BUSINESS.phone}.</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="flex items-center justify-between mt-8">
          <button onClick={back} disabled={step === 0} className="btn-outline disabled:opacity-40 disabled:pointer-events-none" data-testid="booking-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={next} className="btn-primary" data-testid="booking-next-btn">Continue <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={submit} disabled={status === "submitting"} className="btn-accent disabled:opacity-60" data-testid="booking-submit-btn">
              {status === "submitting" ? "Submitting…" : "Submit request"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="mt-8 text-center text-sm text-stoneish">
          Prefer to talk? Call us at <a href={BUSINESS.phoneHref} className="font-semibold text-terracotta hover:text-espresso" data-testid="booking-phone-link">{BUSINESS.phone}</a>
        </div>
      </div>
    </>
  );
}
